/**
 * PushHealthWidget — admin dashboard tile showing real-world push
 * notification delivery health. Pulls from /api/admin/push-metrics which
 * aggregates push_audit rows (send-time success + SW-reported delivered
 * + click-through). Lets us measure whether we ACTUALLY need an SMS /
 * WhatsApp fallback channel before spending money on one.
 *
 * Big number: missed-push rate over the selected window (default 7 days).
 *   <2%  → push is reliable; skip SMS/WhatsApp entirely.
 *   2-5% → tolerable; add WhatsApp only for non-critical notifications.
 *   >5%  → unreliable; SMS/WhatsApp fallback recommended for critical alerts.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, AlertTriangle, CheckCircle2, BellRing } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGES = [
  { value: 1,  label: '24h' },
  { value: 7,  label: '7d'  },
  { value: 30, label: '30d' },
];

const OWNER_FILTERS = [
  { value: '',         label: 'All'       },
  { value: 'driver',   label: 'Drivers'   },
  { value: 'customer', label: 'Customers' },
];

const Bar = ({ value, total, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="text-[11px] font-mono text-slate-500 w-12 text-right">{pct}%</div>
    </div>
  );
};

const PushHealthWidget = () => {
  const { authHeaders } = useAdminAuth();
  const [days, setDays] = useState(7);
  const [ownerType, setOwnerType] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const params = { days };
    if (ownerType) params.owner_type = ownerType;
    axios.get(`${API}/admin/push-metrics`, { headers: authHeaders, params })
      .then(({ data }) => { if (alive) { setData(data); setLoading(false); } })
      .catch(() => { if (alive) { setData(null); setLoading(false); } });
    return () => { alive = false; };
  }, [days, ownerType, authHeaders]);

  const missed = data?.missed_pct ?? 0;
  const verdict = missed < 2
    ? { tone: 'good',  label: 'Reliable',   color: '#10B981', icon: CheckCircle2,
        advice: 'Push is reliable. No SMS / WhatsApp fallback needed.' }
    : missed < 5
    ? { tone: 'warn',  label: 'Acceptable', color: '#F59E0B', icon: Activity,
        advice: 'Consider WhatsApp for non-critical messages. Skip SMS for now.' }
    : { tone: 'bad',   label: 'Unreliable', color: '#EF4444', icon: AlertTriangle,
        advice: 'Recommend SMS / WhatsApp fallback for critical alerts.' };
  const VerdictIcon = verdict.icon;

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-6" data-testid="push-health-widget">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#DEECF9' }}>
            <BellRing className="w-4.5 h-4.5" style={{ color: '#0078D4' }} />
          </div>
          <div>
            <div className="text-xs font-bold tracking-widest uppercase text-slate-500">Push delivery health</div>
            <div className="text-[11px] text-slate-400">Real-world lock-screen delivery</div>
          </div>
        </div>
        <div className="flex gap-1">
          {OWNER_FILTERS.map((o) => (
            <button
              key={o.value || 'all'}
              type="button"
              onClick={() => setOwnerType(o.value)}
              data-testid={`push-health-owner-${o.value || 'all'}`}
              className={`px-2 py-1 rounded-md text-[10px] font-semibold border transition ${
                ownerType === o.value
                  ? 'bg-[#0078D4] text-white border-[#0078D4]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-[#0078D4]'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        {RANGES.map((r) => (
          <button
            key={r.value}
            type="button"
            onClick={() => setDays(r.value)}
            data-testid={`push-health-range-${r.value}d`}
            className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border ${
              days === r.value
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
      ) : !data || data.total_sent === 0 ? (
        <div className="py-8 text-center" data-testid="push-health-empty">
          <div className="text-sm text-slate-500 font-semibold">No pushes sent yet in this window.</div>
          <div className="text-xs text-slate-400 mt-1">Send a few orders to start collecting data.</div>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <div className="text-5xl font-bold tracking-tight text-slate-900" data-testid="push-health-missed-pct">
                {missed}%
              </div>
              <div className="text-xs text-slate-500 mt-1">missed delivery rate</div>
            </div>
            <div
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
              style={{ background: `${verdict.color}20`, color: verdict.color }}
              data-testid={`push-health-verdict-${verdict.tone}`}
            >
              <VerdictIcon className="w-3.5 h-3.5" />
              {verdict.label}
            </div>
          </div>

          <div className="space-y-2.5 mb-4">
            <div>
              <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                <span>Sent to push provider</span>
                <span className="font-mono">{data.send_success} / {data.total_sent}</span>
              </div>
              <Bar value={data.send_success} total={data.total_sent} color="#0078D4" />
            </div>
            <div>
              <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                <span>Reached device (SW ack)</span>
                <span className="font-mono">{data.delivered} / {data.send_success}</span>
              </div>
              <Bar value={data.delivered} total={data.send_success} color="#10B981" />
            </div>
            <div>
              <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                <span>Tapped by user</span>
                <span className="font-mono">{data.clicked} / {data.delivered}</span>
              </div>
              <Bar value={data.clicked} total={data.delivered} color="#7C3AED" />
            </div>
          </div>

          <div className="text-[11px] text-slate-500 italic border-t border-slate-100 pt-3" data-testid="push-health-advice">
            {verdict.advice}
          </div>

          {data.per_owner?.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
                Per-recipient breakdown ({data.per_owner.length})
              </summary>
              <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-slate-100 divide-y divide-slate-50">
                {data.per_owner.map((row) => (
                  <div key={`${row.owner_type}-${row.owner_id}`} className="px-2 py-1.5 flex items-center gap-2 text-[11px]">
                    <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 w-12">
                      {row.owner_type[0]}
                    </div>
                    <div className="flex-1 truncate font-mono text-slate-700" title={row.owner_id}>
                      {row.owner_id || 'unknown'}
                    </div>
                    <div className="font-mono text-slate-400 w-10 text-right">{row.sent}</div>
                    <div
                      className={`w-12 text-right font-bold tabular-nums ${
                        row.missed_pct < 2 ? 'text-emerald-600' :
                        row.missed_pct < 5 ? 'text-amber-600' : 'text-red-600'
                      }`}
                    >
                      {row.missed_pct}%
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
};

export default PushHealthWidget;
